import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CalendarX, ChevronRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dashboard tile that surfaces active members whose athletes don't have
 * an active recurring template — i.e., the "ghost" members at risk of
 * silently dropping off the schedule. Click-through links to each
 * athlete's profile so the coach can set up their weekly slot.
 *
 * Sachin spec 2026-02-17: every active member should be on the recurring
 * schedule. This tile is the gap-finder.
 */
export default function MembersWithoutRecurringTile() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/members-without-recurring`, { withCredentials: true });
                if (!cancelled) setData(r.data);
            } catch {
                if (!cancelled) setData({ unscheduled_count: 0, total_active_members: 0, unscheduled: [] });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (loading) {
        return (
            <div data-testid="members-no-recurring-loading" className="border border-zinc-800 bg-zinc-950 rounded-xl p-5 animate-pulse">
                <div className="h-4 bg-zinc-900 rounded w-1/2 mb-2" />
                <div className="h-7 bg-zinc-900 rounded w-1/4" />
            </div>
        );
    }
    if (!data) return null;

    const count = data.unscheduled_count;
    const total = data.total_active_members;

    // Clean state: every member is scheduled.
    if (count === 0) {
        return (
            <div
                data-testid="members-no-recurring-clean"
                className="border border-emerald-900/40 bg-emerald-950/20 rounded-xl px-5 py-4 flex items-center gap-3"
            >
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <div>
                    <div className="text-sm font-semibold text-emerald-200">All {total} active members have an active subscription</div>
                    <div className="text-xs text-emerald-300/70 mt-0.5">Recurring revenue locked in.</div>
                </div>
            </div>
        );
    }

    // Gap detected: show the top N inline with a click-to-expand.
    const VISIBLE_LIMIT = 5;
    const visible = expanded ? data.unscheduled : data.unscheduled.slice(0, VISIBLE_LIMIT);
    const remaining = Math.max(0, count - VISIBLE_LIMIT);

    return (
        <div
            data-testid="members-no-recurring-tile"
            className="border border-amber-900/40 bg-gradient-to-br from-amber-950/30 to-zinc-950 rounded-xl overflow-hidden"
        >
            <div className="px-5 py-4 border-b border-amber-900/30 flex items-center gap-3">
                <CalendarX className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <div className="flex-1">
                    <div className="text-sm font-semibold text-amber-100">
                        <span data-testid="members-no-recurring-count">{count}</span> active member{count > 1 ? "s" : ""} without an active subscription
                    </div>
                    <div className="text-xs text-amber-200/70 mt-0.5">
                        {count} of {total} active members · start a weekly subscription so they auto-charge every week
                    </div>
                </div>
            </div>

            <div className="divide-y divide-zinc-900">
                {visible.map((m) => (
                    <Link
                        key={`${m.user_id}::${m.athlete_id}`}
                        to={`/coach/leads/${m.user_id}?tab=athletes`}
                        data-testid={`members-no-recurring-row-${m.athlete_id}`}
                        className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-zinc-900/50 transition-colors"
                    >
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-white truncate">
                                {m.athlete_name}
                                <span className="ml-2 text-xs text-zinc-400 font-normal">({m.parent_name})</span>
                            </div>
                            <div className="text-[11px] text-zinc-500 truncate">
                                {m.email}
                                {m.last_booking_at && (
                                    <span className="ml-2">· last booked {new Date(m.last_booking_at).toLocaleDateString()}</span>
                                )}
                                {!m.last_booking_at && <span className="ml-2 text-amber-300">· never booked</span>}
                            </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                    </Link>
                ))}
            </div>

            {!expanded && remaining > 0 && (
                <button
                    type="button"
                    data-testid="members-no-recurring-expand"
                    onClick={() => setExpanded(true)}
                    className="w-full px-5 py-2.5 text-xs text-amber-300 hover:bg-zinc-900/50 border-t border-zinc-900 transition-colors"
                >
                    Show {remaining} more →
                </button>
            )}
        </div>
    );
}
